import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import graphql from "babel-plugin-relay/macro";
import { format, parse } from "date-fns";
import { useFragment } from "react-relay";

import { useEventLocationLabel } from "@/features/Events/hooks/useEventLocationLabel";
import { formatEventTime } from "@/features/Events/utils";
import {
  EventCard,
  EventCardProps,
} from "@whoosh/design-system/src/apps/PatronApp/components/EventCard/EventCard";
import { EventStatusType } from "@whoosh/design-system/src/apps/PatronApp/components/EventCard/EventStatus";
import { Box } from "@whoosh/design-system/src/components/primitive";

import { ClubStackNavigatorParamList } from "../../../../../../navigators/ClubNavigator";
import {
  ClientEventSignUpAccess,
  EventSignUpStatus,
  ClientEventSignUpStatus,
} from "./__generated__/EventCardWrapper_Event.graphql";
import { EventCardWrapper_Event$key } from "./__generated__/EventCardWrapper_Event.graphql";

type EventCardWrapperProps = {
  eventCardWrapperRef: EventCardWrapper_Event$key | null;
  fullWidth?: boolean;
};

export const matchEventStatus = ({
  eventSignUpStatus,
  signUpStatusForClient,
  accessStatusForClient,
}: {
  eventSignUpStatus?: EventSignUpStatus | null;
  signUpStatusForClient?: ClientEventSignUpStatus | null;
  accessStatusForClient?: ClientEventSignUpAccess | null;
}): EventStatusType => {
  if (accessStatusForClient === "CLOSED") {
    return "CLOSED";
  }

  if (eventSignUpStatus === "DRAFT") {
    return "DRAFT";
  }

  const status =
    signUpStatusForClient !== "NONE"
      ? signUpStatusForClient
      : accessStatusForClient;
  switch (status) {
    case "CHECKED_IN":
    case "CONFIRMED":
      return "CONFIRMED";
    case "FULL":
      return "FULL";
    case "PENDING":
      return "PENDING";
    case "SIGNUP_AVAILABLE":
      return "SIGNUP_AVAILABLE";
    case "WAITLIST_AVAILABLE":
      return "WAITLIST_AVAILABLE";
    case "WAITLIST":
      return "ON_WAITLIST";
    default:
      return "COMPLETE";
  }
};

export const EventCardWrapper = ({
  eventCardWrapperRef,
  fullWidth,
}: EventCardWrapperProps) => {
  const navigation =
    useNavigation<NativeStackNavigationProp<ClubStackNavigatorParamList>>();

  const event = useFragment(
    EventCardWrapperFragmentString,
    eventCardWrapperRef
  );

  const firstEventSession =
    event?.eventSessions?.edges?.find((edge) => edge?.node?.startAt)?.node ??
    null;
  const locationLabel = useEventLocationLabel({
    eventSessionRef: firstEventSession,
  });

  const eventSession = event?.eventSessions?.edges?.[0]?.node ?? null;

  const eventTime = formatEventTime({
    displayTime: eventSession?.displayTime,
    startAt: eventSession?.startAt,
    endAt: eventSession?.endAt,
  });

  const formattedEvent: EventCardProps = {
    subTitle: event?.eventSeries?.name || "",
    eventDate: formattedDate(event?.startOn || ""),
    eventTime,
    onPress: () => {
      if (event?.id) navigation.push("EventDetail", { eventID: event.id });
    },
    eventColor: event?.eventSeries?.color || "A",
    publicId: event?.eventSeries?.image?.publicId || "",
    eventStatus: matchEventStatus({
      eventSignUpStatus: event?.signUpForClient?.status,
      signUpStatusForClient: event?.signUpStatusForClient,
      accessStatusForClient: event?.accessStatusForClient,
    }),
    opensAt:
      event?.clientEligibleSignUpWindows?.filter((suw) => !!suw?.opensAt)?.[0]
        ?.opensAt || "",
    departmentType: event?.eventSeries?.facility?.department?.type || "GOLF",
    departmentName: event?.eventSeries?.facility?.department?.name || "Golf",
    spotsAvailable: event?.spotsAvailable || 0,
    tags: event?.tags?.edges?.map((tag) => tag?.node?.name ?? "") || [],
    locationLabel,
  };

  return (
    <Box width={fullWidth ? "100%" : 304} flexGrow={1} flexShrink={0}>
      <EventCard {...formattedEvent} isFeatured />
    </Box>
  );
};

export const EventCardWrapperFragmentString = graphql`
  fragment EventCardWrapper_Event on Event {
    id
    accessStatusForClient(clientId: $memberId)
    accessStatus
    signUpStatusForClient(clientId: $memberId)
    signUpForClient(clientId: $memberId) {
      status
    }
    startOn
    endOn
    tags(first: 1000) {
      edges {
        node {
          name
        }
      }
    }
    spotsAvailable
    eventSessions(first: 1000) {
      edges {
        node {
          ...useEventLocationLabel_Event
          startAt
          endAt
          displayTime
          courses {
            edges {
              node {
                id
                name
                courseBucket {
                  name
                }
              }
            }
          }
        }
      }
    }
    clientEligibleSignUpWindows(clientId: $memberId) {
      id
      closesAt
      opensAt
    }
    eventSeries {
      name
      color
      facility {
        copyOverrides {
          course {
            plural
          }
        }
        department {
          type
          name
        }
      }
      image {
        # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
        publicId: renderingKey
      }
    }
  }
`;

const formattedDate = (startOn: string) => {
  const dateObj = startOn ? parse(startOn, "yyyy-MM-dd", new Date()) : null;

  const dateFormatted = dateObj ? `${format(dateObj, "EEEE, MMM do")}` : "";

  return dateFormatted;
};
